<template>
  <BModal
    v-model="modal"
    title="Add Transaction"
    ref="modalRef"
    ok-title="Save"
    ok-variant="success"
    @ok="handleTransactionSubmission"
  >
    <div class="text-center">
      <BFormGroup
        id="input-group-institution"
        label="Institution:"
        label-for="institution-options"
        class="floating-group"
      >
        <BFormSelect
          id="institution-options"
          v-model="institution"
          :class="{ 'is-invalid': instError !== null }"
          :options="institutionOpts"
          style="font-size: 0.85rem"
        />
      </BFormGroup>
      <BFormGroup
        id="input-group-amount"
        label="Amount:"
        label-for="amount-input"
        class="floating-group"
      >
        <BFormInput
          id="amount-input"
          v-model="amount"
          type="number"
          step="0.01"
          :class="{ 'is-invalid': amountError !== null }"
          style="font-size: 0.78rem"
        />
      </BFormGroup>
      <BFormGroup
        id="input-group-date"
        label="Date:"
        label-for="date-input"
        class="floating-group"
      >
        <BFormInput
          id="date-input"
          v-model="date"
          type="date"
          :class="{ 'is-invalid': dateError !== null }"
          style="font-size: 0.85rem"
        />
      </BFormGroup>
      <BFormGroup
        id="input-group-description"
        label="Description:"
        label-for="description-input"
        class="floating-group"
      >
        <BFormTextarea
          id="description-input"
          v-model="description"
          :class="{ 'is-invalid': descriptionError !== null }"
          placeholder="Enter a brief description..."
          rows="3"
          style="font-size: 0.85rem"
        />
      </BFormGroup>
      <BFormGroup
        id="input-group-note"
        label="Notes:"
        label-for="note-input"
        class="floating-group"
      >
        <BFormTextarea
          id="note-input"
          v-model="note"
          :class="{ 'is-invalid': noteError !== null }"
          placeholder="Enter any additional details..."
          rows="3"
          style="font-size: 0.85rem"
        />
      </BFormGroup>
    </div>
  </BModal>
</template>
<script>
import {
  BModal,
  BFormSelect,
  BFormGroup,
  BFormInput,
  BFormTextarea,
} from "bootstrap-vue-next";
import { userSession as userSessNs } from "@/store/namespaces";

export default {
  name: "CreateTransactionModal",
  components: {
    BModal,
    BFormSelect,
    BFormGroup,
    BFormInput,
    BFormTextarea,
  },
  props: {
    institutionOpts: {
      type: Array,
      required: true,
    },
    acctUid: {
      type: String,
      required: true,
    },
  },
  emits: ["transactionCreated"],
  data() {
    return {
      modal: false,
      amount: null,
      amountError: null, // Is required and can't be 0
      date: null,
      dateError: null, // Is required
      description: null,
      descriptionError: null, // Is required
      note: null,
      noteError: null, // Is optional
      institution: null,
      instError: null, // Is required
    };
  },
  methods: {
    validateForm() {
      let isValid = true;

      if (this.amount === null /* || this.amount === 0*/) {
        this.amountError = "Amount is required and cannot be unset.";
        isValid = false;
      } else {
        this.amountError = null;
      }

      if (!this.date) {
        this.dateError = "Date is required.";
        isValid = false;
      } else {
        this.dateError = null;
      }

      if (!this.description) {
        this.descriptionError = "Description is required.";
        isValid = false;
      } else {
        this.descriptionError = null;
      }

      if (!this.institution) {
        this.instError = "Institution selection is required.";
        isValid = false;
      } else {
        this.instError = null;
      }

      return isValid;
    },
    async handleTransactionSubmission(bvEvt) {
      try {
        bvEvt.preventDefault(); // Prevent the modal from closing immediately
        const formIsValid = this.validateForm();
        if (!formIsValid) return;

        if (!this.acctUid) {
          this.$notify.notifyError(
            "Account context is missing. Please select an account and try again."
          );
          return;
        }

        const userInfo = await this.$store.dispatch(
          `${userSessNs}/getUserInfo`
        );
        if (!userInfo?.id) {
          this.$notify.notifyError(
            "User information is missing. Please log in again and try."
          );
          return;
        }

        await this.$api.post(
          `/banking-institutions/${this.institution.uid}/transactions`,
          {
            acct_uid: this.acctUid,
            user_uid: userInfo.id,
            institution_name: this.institution.name,
            transactions: [
              {
                amount: Number(this.amount).toFixed(2), // Ensure amount is sent as a string with 2 decimal places
                date: this.date,
                description: this.description,
                ...(this.note && this.note.trim() !== ""
                  ? { note: this.note }
                  : {}),
              },
            ],
          }
        );

        this.$refs.modalRef.hide();
        this.$notify.notifySuccess("Transaction saved successfully.");
        this.$emit("transactionCreated");
        this.resetModalState();
      } catch (error) {
        console.error("Error submitting transaction:", error);
        this.$notify.notifyError(
          "An error occurred while saving the transaction. Please try again."
        );
      }
    },
    resetModalState() {
      this.amount = null;
      this.date = null;
      this.description = null;
      this.institution = null;
      this.amountError = null;
      this.dateError = null;
      this.descriptionError = null;
      this.instError = null;
      this.note = null;
      this.noteError = null;
    },
  },
};
</script>
